import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Expense Anomaly Detection Simulation - StyleHub Retail Group
 * 
 * Case Study Context:
 * - 450 retail locations, $1.2B revenue, 8,500 employees
 * - 2.3M expense transactions annually
 * - Before: 3% reviewed, 12-month detection time, $1.2M fraud detected
 * - After: 100% reviewed, real-time detection, $4.2M fraud detected
 * - Ensemble ML: Isolation Forest + Autoencoder + Statistical Control
 * 
 * Visualization:
 * - Real-time transaction stream (2.3M/year ≈ 6,300/day)
 * - Anomaly detection with risk scores (0-100)
 * - Color coding: Green (normal), Yellow (suspicious), Red (high risk)
 * - Shows different fraud patterns: duplicates, policy violations, unusual amounts
 * - Detection precision: 91% (9% false positive rate)
 */

const ExpenseSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    interface Transaction {
      id: string;
      amount: number;
      category: string;
      vendor: string;
      employee: string;
      riskScore: number;
      isAnomaly: boolean;
      fraudType: string;
      x: number;
      y: number;
      vx: number;
      vy: number;
      age: number;
    }
    
    let transactions: Transaction[] = [];
    let detectedAnomalies = 0;
    let totalTransactions = 0;
    let fraudDetected = 0;
    let falsePositives = 0;
    let paused = false;
    let speed = 1;
    let showAnomaliesOnly = false;
    // let selectedCategory = 'all'; // Currently unused
    
    const categories = ['Travel', 'Supplies', 'Equipment', 'Services', 'Entertainment'];
    const fraudTypes = [
      'Duplicate Payment',
      'Personal Expense',
      'Policy Violation',
      'Unusual Amount',
      'Suspicious Vendor',
      'Normal'
    ];
    
    // Detection history
    let detectionHistory: number[] = [];
    let precisionHistory: number[] = [];
    
    p.setup = () => {
      p.createCanvas(800, 500);
      p.frameRate(30);
      
      // Initialize history
      for (let i = 0; i < 60; i++) {
        detectionHistory.push(0);
        precisionHistory.push(91);
      }
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on a transaction
        for (let i = transactions.length - 1; i >= 0; i--) {
          const t = transactions[i];
          const d = p.dist(p.mouseX, p.mouseY, t.x, t.y);
          if (d < 8) {
            // Show transaction details
            console.log('Transaction:', t);
            return false;
          }
        }
        paused = !paused;
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        transactions = [];
        detectedAnomalies = 0;
        totalTransactions = 0;
        fraudDetected = 0;
        falsePositives = 0;
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key === 'a' || p.key === 'A') showAnomaliesOnly = !showAnomaliesOnly;
      if (p.key === 'h' || p.key === 'H') {
        // Show high risk only
        showAnomaliesOnly = true;
      }
      if (p.key === 'n' || p.key === 'N') {
        // Show all
        showAnomaliesOnly = false;
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused) {
        // Generate new transactions (6,300 per day ≈ 0.07 per frame at 30fps)
        if (Math.random() < 0.15 * speed) {
          generateTransaction();
        }
        
        // Update existing transactions
        updateTransactions();
      }
      
      // Draw visualization
      drawTitle();
      drawTransactionStream();
      drawRiskDistribution();
      drawMetrics();
      drawLegend();
      drawControls();
    };
    
    const generateTransaction = () => {
      totalTransactions++;
      
      // 0.08% are anomalies (1,847 out of 2.3M from case study)
      const isAnomaly = Math.random() < 0.0008 * 10; // Amplified for visualization
      
      let riskScore = 0;
      let fraudType = 'Normal';
      
      if (isAnomaly) {
        // Generate anomalous transaction
        riskScore = 75 + Math.random() * 25; // 75-100
        const fraudIndex = Math.floor(Math.random() * (fraudTypes.length - 1));
        fraudType = fraudTypes[fraudIndex];
        detectedAnomalies++;
        
        // 91% precision means 9% false positives
        if (Math.random() < 0.91) {
          fraudDetected++;
        } else {
          falsePositives++;
        }
      } else {
        // Normal transaction
        riskScore = Math.random() * 60; // 0-60
        fraudType = 'Normal';
      }
      
      const transaction: Transaction = {
        id: `TXN-${totalTransactions.toString().padStart(7, '0')}`,
        amount: isAnomaly ? 
          (Math.random() * 50000 + 5000) : // Anomalies: $5K-$55K
          (Math.random() * 2000 + 50),     // Normal: $50-$2K
        category: categories[Math.floor(Math.random() * categories.length)],
        vendor: `Vendor ${Math.floor(Math.random() * 1200)}`,
        employee: `EMP${Math.floor(Math.random() * 8500)}`,
        riskScore: riskScore,
        isAnomaly: isAnomaly,
        fraudType: fraudType,
        x: p.width + 20,
        y: p.random(100, 400),
        vx: -2 - Math.random() * 2,
        vy: (Math.random() - 0.5) * 0.5,
        age: 0
      };
      
      transactions.push(transaction);
      
      // Update history
      detectionHistory.push(detectedAnomalies);
      if (detectionHistory.length > 60) detectionHistory.shift();
      
      const precision = totalTransactions > 0 ? 
        (fraudDetected / (fraudDetected + falsePositives)) * 100 : 91;
      precisionHistory.push(precision);
      if (precisionHistory.length > 60) precisionHistory.shift();
    };
    
    const updateTransactions = () => {
      for (let i = transactions.length - 1; i >= 0; i--) {
        const t = transactions[i];
        t.x += t.vx * speed;
        t.y += t.vy * speed;
        t.age++;
        
        // Remove if off screen or too old
        if (t.x < -20 || t.age > 300) {
          transactions.splice(i, 1);
        }
      }
    };
    
    const drawTitle = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('StyleHub Retail Group - Expense Anomaly Detection', 20, 15);
      
      p.textSize(11);
      p.fill(100);
      p.text('Ensemble ML: Isolation Forest + Autoencoder + Statistical Control | 2.3M transactions/year', 20, 35);
      
      p.textSize(10);
      p.text(`Processing: ${totalTransactions.toLocaleString()} transactions | Real-time scoring`, 20, 55);
    };
    
    const drawTransactionStream = () => {
      const streamY = 100;
      const streamH = 300;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(20, streamY, 500, streamH);
      
      // Risk zones
      p.noStroke();
      p.fill(255, 200, 200, 30);
      p.rect(20, streamY, 500, streamH * 0.25); // High risk zone (top)
      
      p.fill(255, 255, 200, 30);
      p.rect(20, streamY + streamH * 0.25, 500, streamH * 0.25); // Medium risk
      
      p.fill(200, 255, 200, 30);
      p.rect(20, streamY + streamH * 0.5, 500, streamH * 0.5); // Low risk
      
      // Zone labels
      p.fill(100);
      p.textAlign(p.RIGHT, p.TOP);
      p.textSize(9);
      p.text('High Risk (>75)', 515, streamY + 5);
      p.text('Medium Risk (60-75)', 515, streamY + streamH * 0.25 + 5);
      p.text('Low Risk (<60)', 515, streamY + streamH * 0.5 + 5);
      
      // Draw transactions
      transactions.forEach(t => {
        if (showAnomaliesOnly && !t.isAnomaly) return;
        
        // Map risk score to Y position
        t.y = p.map(t.riskScore, 0, 100, streamY + streamH - 10, streamY + 10);
        
        // Color based on risk
        let color;
        if (t.riskScore > 75) {
          color = p.color(255, 100, 100, 200); // Red - high risk
        } else if (t.riskScore > 60) {
          color = p.color(255, 200, 100, 180); // Orange - medium
        } else {
          color = p.color(100, 200, 100, 160); // Green - low
        }
        
        p.fill(color);
        p.noStroke();
        const size = t.isAnomaly ? 10 : 6;
        p.circle(t.x, t.y, size);
        
        // Highlight flagged anomalies
        if (t.isAnomaly && t.riskScore > 75) {
          p.noFill();
          p.stroke(255, 0, 0, 150);
          p.strokeWeight(2);
          p.circle(t.x, t.y, size + 4);
        }
      });
      
      // Current transaction details (rightmost)
      const recentTxn = transactions[transactions.length - 1];
      if (recentTxn && recentTxn.x > 400) {
        p.fill(40);
        p.noStroke();
        p.textAlign(p.LEFT, p.TOP);
        p.textSize(8);
        const detailX = 30;
        const detailY = streamY + streamH + 10;
        
        p.text(`Latest: ${recentTxn.id}`, detailX, detailY);
        p.text(`Amount: $${recentTxn.amount.toFixed(2)}`, detailX, detailY + 10);
        p.text(`Risk: ${recentTxn.riskScore.toFixed(0)}`, detailX + 150, detailY);
        
        if (recentTxn.isAnomaly) {
          p.fill(255, 100, 100);
          p.text(`⚠️  ${recentTxn.fraudType}`, detailX + 150, detailY + 10);
        } else {
          p.fill(100, 180, 100);
          p.text(`✓ Normal`, detailX + 150, detailY + 10);
        }
      }
    };
    
    const drawRiskDistribution = () => {
      const chartX = 550;
      const chartY = 100;
      const chartW = 230;
      const chartH = 150;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      p.text('Risk Score Distribution', chartX + 10, chartY + 10);
      
      // Create histogram
      const bins = 10;
      const histogram = new Array(bins).fill(0);
      
      transactions.forEach(t => {
        const bin = Math.min(Math.floor(t.riskScore / 10), bins - 1);
        histogram[bin]++;
      });
      
      const maxCount = Math.max(...histogram, 1);
      const barW = (chartW - 40) / bins;
      
      for (let i = 0; i < bins; i++) {
        const barH = (histogram[i] / maxCount) * (chartH - 50);
        const x = chartX + 20 + i * barW;
        const y = chartY + chartH - 20 - barH;
        
        // Color based on risk level
        let color;
        if (i >= 7) color = p.color(255, 100, 100);
        else if (i >= 6) color = p.color(255, 200, 100);
        else color = p.color(100, 200, 100);
        
        p.fill(color);
        p.noStroke();
        p.rect(x, y, barW - 2, barH);
        
        // Label
        p.fill(100);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(7);
        p.text(`${i * 10}`, x + barW / 2, chartY + chartH - 15);
      }
      
      // X-axis label
      p.fill(100);
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(9);
      p.text('Risk Score', chartX + chartW / 2, chartY + chartH - 5);
    };
    
    const drawMetrics = () => {
      const metricsY = 270;
      const metricsX = 550;
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      
      p.text('Detection Performance', metricsX, metricsY);
      
      p.textSize(10);
      let y = metricsY + 20;
      const lineH = 16;
      
      p.fill(100);
      p.text('Total Processed:', metricsX, y);
      p.fill(40);
      p.text(totalTransactions.toLocaleString(), metricsX + 120, y);
      y += lineH;
      
      p.fill(100);
      p.text('Anomalies Detected:', metricsX, y);
      p.fill(255, 100, 100);
      p.text(detectedAnomalies.toString(), metricsX + 120, y);
      y += lineH;
      
      p.fill(100);
      p.text('Fraud Confirmed:', metricsX, y);
      p.fill(200, 100, 100);
      p.text(fraudDetected.toString(), metricsX + 120, y);
      y += lineH;
      
      p.fill(100);
      p.text('False Positives:', metricsX, y);
      p.fill(200, 150, 50);
      p.text(falsePositives.toString(), metricsX + 120, y);
      y += lineH;
      
      const precision = totalTransactions > 0 && (fraudDetected + falsePositives) > 0 ? 
        (fraudDetected / (fraudDetected + falsePositives)) * 100 : 91;
      
      p.fill(100);
      p.text('Precision:', metricsX, y);
      p.fill(precision > 85 ? p.color(100, 180, 100) : p.color(200, 150, 50));
      p.text(`${precision.toFixed(1)}%`, metricsX + 120, y);
      y += lineH;
      
      // Estimated annual impact
      const annualRate = (detectedAnomalies / Math.max(totalTransactions, 1)) * 2300000;
      const estimatedFraud = annualRate * 2000; // Avg $2K per fraud
      
      y += 10;
      p.fill(100);
      p.textSize(9);
      p.text(`Projected Annual Detection:`, metricsX, y);
      y += 12;
      p.fill(40);
      p.text(`${annualRate.toFixed(0)} anomalies`, metricsX, y);
      y += 12;
      p.text(`$${(estimatedFraud / 1000000).toFixed(1)}M fraud prevented`, metricsX, y);
    };
    
    const drawLegend = () => {
      const legendX = 550;
      const legendY = 430;
      
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(9);
      p.fill(100);
      p.text('Legend:', legendX, legendY);
      
      // Colors
      p.fill(255, 100, 100);
      p.circle(legendX + 5, legendY + 15, 8);
      p.fill(100);
      p.text('High Risk (>75)', legendX + 15, legendY + 11);
      
      p.fill(255, 200, 100);
      p.circle(legendX + 5, legendY + 28, 8);
      p.fill(100);
      p.text('Medium Risk (60-75)', legendX + 15, legendY + 24);
      
      p.fill(100, 200, 100);
      p.circle(legendX + 5, legendY + 41, 8);
      p.fill(100);
      p.text('Low Risk (<60)', legendX + 15, legendY + 37);
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.BOTTOM);
      p.textSize(9);
      
      p.text('Click Transaction: Details | A: Toggle Anomalies Only | H: High Risk | N: Show All | Space: Pause | R: Reset | +/-: Speed', 20, 490);
      
      p.textAlign(p.RIGHT, p.BOTTOM);
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : `RUNNING (${speed.toFixed(1)}x)`, 780, 490);
      
      if (showAnomaliesOnly) {
        p.fill(255, 100, 100);
        p.text('ANOMALIES ONLY', 680, 490);
      }
    };
  };

  return (
    <BaseSimulation
      sketch={sketch}
      title="Expense Anomaly Detection Simulation"
      description="Interactive visualization of StyleHub Retail's expense fraud detection using ensemble machine learning (Isolation Forest + Autoencoder + Statistical Control). Shows real-time transaction processing and anomaly scoring."
    />
  );
};

export default ExpenseSimulation;
